import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import { useTheme } from '../../contexts/ThemeContext';

const SignIn = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const handleSignIn = async (e) => {
    e?.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService?.signIn(email, password);
      navigate('/family-calendar-view');
    } catch (err) {
      setError(err?.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleDemoLogin = (demoEmail) => {
    setEmail(demoEmail);
    setPassword('password123');
  };

  return (
    <>
      <Helmet>
        <title>Sign In - Starling Schedule</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-card rounded-xl shadow-warm-xl p-8">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <AppImage
                src="/assets/images/ad4cbaf4-dc2b-4059-a1d5-7af12fa38f76_copy-1767800870676.jpg"
                alt="Starling Family Planner Logo"
                className="w-24 h-24 object-contain"
              />
            </div>

            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-h2 font-heading text-foreground mb-2">
                Welcome Back
              </h1>
              <p className="text-muted-foreground">
                Sign in to your family planner
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
                <Icon name="alert-circle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Sign In Form */}
            <form onSubmit={handleSignIn} className="space-y-4 mb-6">
              <Input
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                placeholder="your@email.com"
                required
              />

              <Input
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e?.target?.value)}
                placeholder="Enter your password"
                required
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                iconName={loading ? 'loader' : 'log-in'}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Demo Credentials */}
            <div className="mb-6 p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Icon name="info" size={16} />
                Demo Credentials:
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => handleDemoLogin('sarah@example.com')}
                  className="w-full text-left text-sm p-2 rounded hover:bg-background transition-colors"
                >
                  <span className="text-muted-foreground">Primary Admin:</span>
                  <br />
                  <span className="text-foreground font-mono">sarah@example.com / password123</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDemoLogin('michael@example.com')}
                  className="w-full text-left text-sm p-2 rounded hover:bg-background transition-colors"
                >
                  <span className="text-muted-foreground">Family Member:</span>
                  <br />
                  <span className="text-foreground font-mono">michael@example.com / password123</span>
                </button>
              </div>
            </div>

            {/* Links */}
            <div className="text-center space-y-3">
              <Link 
                to="/forgot-password" 
                className="text-sm text-primary hover:underline block"
              >
                Forgot your password?
              </Link>
              <div className="text-sm text-muted-foreground">
                Do not have an account?{' '}
                <Link to="/signup" className="text-primary hover:underline font-medium">
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SignIn;